import React, { useContext, useEffect, useRef } from 'react';
import { CreditCard } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { centsToDollars } from 'shared-components/src/utils/currency';
import {
  Container,
  CenterHeader,
  SuccessIconCircle,
  Title,
  Subtitle,
  InfoAlert,
  InfoRow,
  InfoTitle,
  InfoText,
  Card,
  CardHeaderRow,
  DetailsList,
  Label,
  ValueStrong,
  Value,
  Mono,
  ButtonsRow,
  PrintableDiv,
  OutlineButton,
} from './styles';
import { ProgressBar, ProgressFill } from '../style';
import { DisbursementsContext } from '../../../contexts/DisbursementsContext';
import PrintOnlyStyles, { printSamePage } from './PrintOnlyStyles';
import {
  downloadElementAsPDF,
  shareElementAsImage,
} from '../shareDownloadHelpers';
import { formatIntlCurrency } from '../../../utils/helpers';
import {
  CheckGreenIcon,
  DownloadIcon,
  InfoIcon,
  ShareIcon,
} from '../../../components/Icons';

function TransactionSuccess() {
  const navigate = useNavigate();
  const printableRef = useRef(null);
  const intl = useIntl();
  const { transferSuccessData = {} } = useContext(DisbursementsContext);
  const {
    amount = 0,
    processor_reference: processorReference = '',
    id: transactionId = '',
    transfer = {},
  } = transferSuccessData || {};
  const { amount: transferAmount = 0, fee = 0, transferred_at: transferredAt } = transfer || {};
  const originalAmount = formatIntlCurrency(
    centsToDollars(amount || 0),
    intl.locale
  );
  const amountTranferred = formatIntlCurrency(
    centsToDollars(transferAmount || 0),
    intl.locale
  );
  const transferFee = formatIntlCurrency(centsToDollars(fee || 0), intl.locale);
  const transferDate = new Date(transferredAt).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const urlParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  );
  const isExceptionScenario = urlParams.get('exception') === 'completed';

  const handleShare = async () => {
    const el = printableRef.current;
    if (!el) return;

    const ok = await shareElementAsImage(el, {
      title: intl.messages['success.title'],
      text: intl.messages['success.subtitle.completed'],
    });

    if (!ok) {
      // Fallback: same-page print if Web Share isn’t supported
      printSamePage('#printable');
    }
  };

  const handleDownload = async () => {
    const el = printableRef.current;
    if (!el) return;
    await downloadElementAsPDF(el, {
      fileName: `receipt-${transactionId}.pdf`,
    });
  };

  useEffect(() => {
    if (!transferSuccessData) {
      navigate('/disbursements/redeem');
    }
  }, [transferSuccessData, navigate]);

  return (
    <>
      <PrintOnlyStyles />
      {/* Progress Bar - Complete */}
      <ProgressBar>
        <ProgressFill />
      </ProgressBar>

      <Container>
        {/* Already Completed Alert - Only show for exception scenario */}
        {isExceptionScenario && (
          <InfoAlert>
            <InfoRow>
              <InfoIcon style={{ flexShrink: 0 }} />
              <div>
                <InfoTitle>{intl.messages['success.alert.title']}</InfoTitle>
              </div>
            </InfoRow>
            <InfoText>{intl.messages['success.alert.message']}</InfoText>
          </InfoAlert>
        )}

        <PrintableDiv id="printable" ref={printableRef}>
          {/* Success Icon and Header */}
          <CenterHeader>
            <SuccessIconCircle>
              <CheckGreenIcon />
            </SuccessIconCircle>
            <Title>{intl.messages['success.title']}</Title>
            <Subtitle>{intl.messages['success.subtitle.completed']}</Subtitle>
          </CenterHeader>

          {/* Transaction Details Card */}
          <Card>
            <CardHeaderRow>
              <CreditCard size={32} color="#065f46" />
              <h2
                style={{
                  margin: 0,
                  fontWeight: 600,
                  fontSize: 24,
                  color: '#231F20',
                }}
              >
                {intl.messages['success.details.title']}
              </h2>
            </CardHeaderRow>

            <DetailsList>
              <div>
                <Label>{intl.messages['success.amount']}</Label>
                <ValueStrong>{amountTranferred}</ValueStrong>
              </div>

              <div>
                <Label>{intl.messages['success.fee']}</Label>
                <Value>{transferFee}</Value>
              </div>

              <div>
                <Label>{intl.messages['success.original']}</Label>
                <Value>{originalAmount}</Value>
              </div>

              <div>
                <Label>{intl.messages['success.id']}</Label>
                <Mono>{transactionId}</Mono>
              </div>

              <div>
                <Label>{intl.messages['success.processor_reference']}</Label>
                <Mono>{processorReference}</Mono>
              </div>

              <div>
                <Label>{intl.messages['success.method']}</Label>
                <Value>Visa Direct</Value>
              </div>

              <div>
                <Label>{intl.messages['success.date']}</Label>
                <Value>{transferDate}</Value>
              </div>
            </DetailsList>
          </Card>
        </PrintableDiv>

        {/* Receipt Buttons */}
        <ButtonsRow>
          <OutlineButton onClick={handleDownload}>
            <DownloadIcon />
            {intl.messages['success.download']}
          </OutlineButton>

          <OutlineButton id="share-button" onClick={handleShare}>
            <ShareIcon />
            {intl.messages['success.share']}
          </OutlineButton>
        </ButtonsRow>
      </Container>
    </>
  );
}

export default TransactionSuccess;
